/**
 * CetusEd Admin Console — Invoice List
 *
 * COMPLIANCE: Invoices are financial records with no PII. They are retained
 *   per the district's financial record retention policy (typically 7 years).
 *   Access to invoices is restricted to users with 'admin:billing:read'
 *   capability.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInvoices } from '../../api/hooks';
import { DataTable, type Column } from '../../ui/DataTable';
import type { Invoice } from '../../api/hooks';

export const InvoiceList: React.FC = () => {
  const [districtFilter] = useState('');
  const { data: invoices, isLoading, error } = useInvoices(districtFilter || undefined);

  const columns: Column<Invoice>[] = [
    { key: 'invoiceNumber', header: 'Invoice #', sortable: true },
    { key: 'districtName', header: 'District', sortable: true },
    { key: 'amount', header: 'Amount', sortable: true, render: (row) => `$${row.amount.toFixed(2)}` },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => <span className={`status-badge status-badge--${row.status}`}>{row.status}</span>,
    },
    {
      key: 'periodStart',
      header: 'Period',
      render: (row) => `${new Date(row.periodStart).toLocaleDateString()} — ${new Date(row.periodEnd).toLocaleDateString()}`,
    },
    { key: 'dueDate', header: 'Due Date', render: (row) => new Date(row.dueDate).toLocaleDateString() },
    { key: 'paidAt', header: 'Paid At', render: (row) => row.paidAt ? new Date(row.paidAt).toLocaleDateString() : '—' },
  ];

  if (error) {
    return (
      <div className="admin-error" role="alert">
        Unable to load invoices.
      </div>
    );
  }

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/billing">Billing</Link> / Invoices
      </nav>

      <div className="page-header">
        <h1>Invoices</h1>
      </div>

      <DataTable
        columns={columns}
        data={invoices || []}
        keyExtractor={(i) => i.id}
        isLoading={isLoading}
        emptyMessage="No invoices found."
        pageSize={15}
      />
    </div>
  );
};
